import { memo, ReactNode } from "react";
import classNames from "classnames";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { EmptyValue } from "@design-system/EmptyValue/EmptyValue";
import { GroupPathLabel } from "@components/forms/GroupSelectField/GroupPathLabel";
import ruleTagLayoutStyles from "@components/transactionRules/RuleTagLayout.module.scss";
import { HistoryDetailsEntityDisplayCell } from "../../cells/HistoryDetailsEntityDisplayCell";
import {
    EMPTY_TEXT,
    type HistoryAccountValue,
} from "../utils/coaActivityValues";
import type {
    HistoryAccountDetailsRow,
    HistoryAccountLine,
} from "../views/historyAccountDetailsViewConfig";
import styles from "./HistoryAccountCells.module.scss";

const MULTI_LINE_ROW_CLASS = "coaActivityLines";

interface HistoryAccountCellProps {
    row: { original: HistoryAccountDetailsRow };
}

interface LinesCellProps {
    lines: HistoryAccountLine[];
    renderLine: (line: HistoryAccountLine, index: number) => ReactNode;
}

const LinesCell = ({ lines, renderLine }: LinesCellProps) => (
    <ViewTableCell
        className={classNames(styles.lines, {
            [MULTI_LINE_ROW_CLASS]: lines.length > 1,
        })}
    >
        {lines.map((line, index) => (
            <div key={line.id} className={styles.line}>
                {renderLine(line, index)}
            </div>
        ))}
    </ViewTableCell>
);

const GroupPathContent = ({ segments }: { segments: string[] }) => (
    <div className={styles.groupPath}>
        <GroupPathLabel segments={segments} />
    </div>
);

const ValueContent = ({ value }: { value: HistoryAccountValue }) => {
    if (value.kind === "notApplicable") {
        return null;
    }
    if (value.kind === "groupPath") {
        return <GroupPathContent segments={value.segments} />;
    }
    return value.text === EMPTY_TEXT ? (
        <EmptyValue />
    ) : (
        <BreakableText text={value.text} showTooltip />
    );
};

export const HistoryAccountIdentifierCell = memo(
    ({ row }: HistoryAccountCellProps) => (
        <LinesCell
            lines={row.original.lines}
            renderLine={(line) => (
                <>
                    {line.label && (
                        <span
                            className={classNames(
                                ruleTagLayoutStyles.label,
                                styles.lineLabel,
                            )}
                        >
                            {line.label}
                        </span>
                    )}
                    <BreakableText text={line.identifier} showTooltip />
                    {line.identifierNote && (
                        <span className={styles.identifierNote}>
                            {line.identifierNote}
                        </span>
                    )}
                </>
            )}
        />
    ),
);

HistoryAccountIdentifierCell.displayName = "HistoryAccountIdentifierCell";

export const HistoryAccountGroupCell = memo(
    ({ row }: HistoryAccountCellProps) => (
        <LinesCell
            lines={row.original.lines}
            renderLine={(line) => (
                <GroupPathContent segments={line.groupPath} />
            )}
        />
    ),
);

HistoryAccountGroupCell.displayName = "HistoryAccountGroupCell";

export const HistoryAccountEntityCell = memo(
    ({ row }: HistoryAccountCellProps) => (
        <LinesCell
            lines={row.original.lines}
            renderLine={(_line, index) =>
                index === 0 && <HistoryDetailsEntityDisplayCell row={row} />
            }
        />
    ),
);

HistoryAccountEntityCell.displayName = "HistoryAccountEntityCell";

export const HistoryAccountPreviousCell = memo(
    ({ row }: HistoryAccountCellProps) => (
        <LinesCell
            lines={row.original.lines}
            renderLine={(line) => <ValueContent value={line.previous} />}
        />
    ),
);

HistoryAccountPreviousCell.displayName = "HistoryAccountPreviousCell";

export const HistoryAccountModifiedCell = memo(
    ({ row }: HistoryAccountCellProps) => (
        <LinesCell
            lines={row.original.lines}
            renderLine={(line) => <ValueContent value={line.modified} />}
        />
    ),
);

HistoryAccountModifiedCell.displayName = "HistoryAccountModifiedCell";
